import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { readableLink, readableMuted, readableText } from "assets/theme/boldText";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Skeleton,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import GridOnIcon from "@mui/icons-material/GridOn";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ordersService from "services/ordersService";
import orgDataService from "services/orgDataService";
import { Client, Hub } from "common/models";
import { EventResetFilter, OrderListItem, OrderSearch } from "orderManagement/models";
import { toast } from "react-toastify";
import { AuthContext } from "common/AuthProvider";
import ConfirmDialog from "common/components/ConfirmDialog";
import EventsService from "services/eventsService";
import BulkEventResetResultsDialog from "./BulkEventResetResultsDialog";
import BulkEventResetOverlay, { BulkResetRun } from "./BulkEventResetOverlay";
import EventResetsService from "services/eventResetsService";
import {
  BulkResetSummary,
  bulkResetConfirmText,
  bulkResetErrorMessage,
  bulkResetSelectionError,
  BULK_EVENT_RESET_CHUNK_SIZE,
  MAX_BULK_EVENT_RESET,
  canBulkResetEvents,
  eligibleTripsSelection,
  isBatchFinished,
  isQueueUnavailable,
  queuedSubmitMessage,
  responseErrorText,
  runBulkEventReset,
  summaryFromBatch,
} from "./bulkEventReset";
import { eventResetsLink } from "./eventResets";
import { EVENT_RESET_FILTERS, restoreEventResetFilters } from "./eventResetFilters";
import LastResetCell from "./LastResetCell";
import { useTripSelection } from "./tripSelection";

// How often a queued run's progress is read back from its batch.
const QUEUED_POLL_MS = 3000;

// Review Queue: un-approved trips that need a reviewer's eyes. Drop-in
// replacement for the legacy dashboard QueueForm.aspx — same trip set,
// same default window (today-2d), same Mgr-Review opt-in toggle.
// OrderApi applies the sproc's hardcoded baseline (TripID floor, exclude
// test driver, require TripReview row) when isReviewQueueMode is true.

const FILTER_STORAGE_KEY = "reviewQueueFilters";
// The date pickers fire onChange per keystroke; ignore half-typed years so they are neither searched nor saved.
const isCompleteDate = (v: unknown) => !!v && moment(v as any).isValid() && moment(v as any).year() >= 2000;
// Sentinel values for the "Select all" / "Clear all" entries at the top of each multi-select dropdown.
const SELECT_ALL_VALUE = "__select_all__";
const CLEAR_ALL_VALUE = "__clear_all__";

// Same unchecked box the Order Management selection column uses, so the two grids read alike.
const UncheckedIcon = () => (
  <Box sx={{ width: 18, height: 18, border: "2px solid rgba(0,0,0,0.6)", borderRadius: "3px" }} />
);

const ReviewQueue = () => {
  const [{ boldText }] = useMaterialUIController();
  const { isUserInRole } = useContext(AuthContext);
  // Sort names OrderApi's DispatchQueueMapper accepts that this screen can produce.
  const SERVER_SORTS = ["tripID", "clientName", "controlNumber", "hub", "pickupDateTime", "driverName"];
  const canResetEvents = canBulkResetEvents(isUserInRole as (role: string) => boolean);

  const getInitialSearch = (): OrderSearch => {
    const persisted = localStorage.getItem(FILTER_STORAGE_KEY);
    // Defaults align with legacy Dashboard QueueForm:
    //  - shows all un-approved trips (open + closed), not closed-only
    //  - 2-day window matches the VB code's StartDate default
    //  - isReviewQueueMode=true triggers OrderApi's baseline filters (TripID floor,
    //    exclude test driver, require TripReview row) that mirror the sproc.
    //  - Mgr Review is opt-in via the checkbox, not default-on.
    const base: OrderSearch = {
      clientIds: null,
      hubIds: null,
      driverHubIds: null,
      closedByName: null,
      driverResponse: null,
      litmusFilter: null,
      eventResetFilter: null,
      isResettable: null,
      tripTypes: null,
      isCancelled: false,
      isClosed: null,
      isApproved: false,
      isVerified: null,
      isMpv: null,
      isBargeClient: null,
      isMapped: null,
      isNearOtp: null,
      isReviewQueueMode: true,
      orderStatusIds: null,
      // Match legacy Dashboard QueueForm defaults exactly: start = today-2d at 00:00,
      // end = BLANK (Dashboard leaves the end date empty, which omits the upper bound and
      // returns everything from the start date forward). A blank end here sends an
      // unbounded sentinel in ordersService. When a date IS picked, it's start-of-day with
      // no time-of-day / offset so identical selections return identical rows (Dashboard's
      // params are cast to SQL `date`).
      startDate: moment().subtract(2, "day").startOf("day"),
      endDate: null,
      alert: null,
      dispatchQueueConfigId: null,
      pagedSearchOptions: { offset: 0, pageSize: 25 },
      sortAsc: false,
      sortName: "pickupDateTime",
    };

    if (persisted) {
      try {
        const parsed = JSON.parse(persisted);
        // Every setting the user changed comes back (the whole search is saved), keyed on
        // the base so a field added later still gets its default. A saved null wins over it.
        const restored: OrderSearch = { ...base };
        for (const key of Object.keys(base) as (keyof OrderSearch)[]) {
          if (key in parsed) (restored as any)[key] = parsed[key];
        }
        return {
          ...restored,
          isReviewQueueMode: true,
          // Dates the user picked are kept; closers were re-entering them on every visit.
          startDate: "startDate" in parsed ? (parsed.startDate ? moment(parsed.startDate) : null) : base.startDate,
          endDate: "endDate" in parsed ? (parsed.endDate ? moment(parsed.endDate) : null) : base.endDate,
          ...restoreEventResetFilters(parsed, canResetEvents),
          pagedSearchOptions: parsed.pagedSearchOptions || base.pagedSearchOptions,
          sortAsc: parsed.sortAsc ?? base.sortAsc,
          // A sort saved before a column became unsortable would 500 on every load.
          sortName: SERVER_SORTS.includes(parsed.sortName) ? parsed.sortName : base.sortName,
        };
      } catch (err) {
        console.warn("Review Queue: saved filters could not be read, using defaults", err);
      }
    }
    return base;
  };

  const [rows, setRows] = useState<OrderListItem[]>([]);
  const [search, setSearch] = useState<OrderSearch>(getInitialSearch());
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [closedByNames, setClosedByNames] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const gridRef = useGridApiRef();
  const { selectedTripIds, allMatchingSelected, selectAll, toggle: toggleTrip, clear: clearSelection } = useTripSelection();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // "Resetting 20 of 37…" while a run is in flight, null otherwise.
  const [resetProgress, setResetProgress] = useState<string | null>(null);
  // Drives the full-page overlay while a bulk reset runs; null when idle.
  const [resetRun, setResetRun] = useState<BulkResetRun | null>(null);
  const [resetSummary, setResetSummary] = useState<BulkResetSummary | null>(null);
  // A queued run (the server does the trips): its batch, and whether its overlay was sent to the background.
  const [queuedBatchId, setQueuedBatchId] = useState<string | null>(null);
  const [overlayInBackground, setOverlayInBackground] = useState(false);
  const navigate = useNavigate();
  const selectionError = bulkResetSelectionError(selectedTripIds.length);

  useEffect(() => {
    orgDataService.getAllHubs().then(setHubs);
    orgDataService.getAllClients().then(setClients);
    ordersService.getClosedByNames().then(setClosedByNames).catch(() => setClosedByNames([]));
  }, []);

  // tri-state select helpers: "" = All, "true"/"false" map to bool
  // Client Type cascades into the Client dropdown: picking Barge narrows it to barge clients, so
  // the list stops offering combinations that return nothing.
  //
  // Trip Location is NOT cascaded yet - that needs a client-to-hub mapping the frontend does not
  // have (clients carry no hubs), so it would take a lookup endpoint deriving hubs from the points
  // of service of clients of the chosen type.
  //
  // Clients whose barge flag never came back (older payloads) stay in the list rather than being
  // hidden: dropping a client from the picker because a field was missing is worse than showing
  // one extra.
  const visibleClients = useMemo(() => {
    if (search.isBargeClient === null || search.isBargeClient === undefined) return clients;
    return clients.filter((c) => c.isBargeClient === undefined || c.isBargeClient === search.isBargeClient);
  }, [clients, search.isBargeClient]);

  const triValue = (v: boolean | null | undefined) => v === null || v === undefined ? "" : v.toString();
  const triParse = (s: string): boolean | null => s === "" ? null : s === "true";

  // Shared dimensions so every input in the 2-column grid lines up vertically.
  const INPUT_WIDTH = 190;
  const inputSx = { width: INPUT_WIDTH };

  // Selection is per page: any change to the search (filters, sort, page) clears it, so a reset
  // can only ever touch trips the reviewer is looking at.
  // Only a closed trip can be reset (OrderApi refuses the rest), so only closed trips get a checkbox.
  const isSelectable = (r: OrderListItem) => r.isClosed === true;
  // The header checkbox selects the first MAX_BULK_EVENT_RESET trips matching the current search that a
  // reset would actually take (across pages: OrderApi works through the same query), never more than
  // one reset may take.
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const atSelectionLimit = selectedTripIds.length >= MAX_BULK_EVENT_RESET;
  // The filters currently on screen (set on every render below). A select-all answer that comes back
  // after the filters changed belongs to trips no longer listed, so it is dropped.
  const currentFilterKeyRef = useRef<string>("");
  const selectAllMatching = async () => {
    const startedFor = currentFilterKeyRef.current;
    setIsSelectingAll(true);
    try {
      const selection = eligibleTripsSelection(await EventResetsService.getEligibleTrips(search, MAX_BULK_EVENT_RESET));
      if (currentFilterKeyRef.current !== startedFor) return;
      selectAll(selection.tripIds);
      if (selection.level === "warning") toast.warning(selection.message);
      else toast.info(selection.message);
    } catch (e) {
      clearSelection();
      toast.error(responseErrorText(e) || "Could not find trips that can be reset. Try again.");
    } finally {
      setIsSelectingAll(false);
    }
  };

  // Column set mirrors legacy Dashboard QueueForm.aspx with two intentional
  // deviations the user asked for: keep Trip # (Dashboard uses an Events link
  // instead), and drop Sheet Recvd / Driver Called columns.
  // The selection column comes first and only for users who can reset events.
  const columns: GridColDef[] = [
    ...(canResetEvents ? [{
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      renderHeader: () => isSelectingAll ? (
        <CircularProgress size={18} sx={{ ml: 1 }} />
      ) : (
        <Checkbox
          size="small"
          icon={<UncheckedIcon />}
          checked={allMatchingSelected}
          indeterminate={selectedTripIds.length > 0 && !allMatchingSelected}
          disabled={isSelectingAll}
          onChange={(e) => (e.target.checked ? selectAllMatching() : clearSelection())}
        />
      ),
      renderCell: (params: GridRenderCellParams<any>) => {
        if (!isSelectable(params.row)) return null;
        const checked = selectedTripIds.includes(params.row.tripID);
        // At the limit, only already-picked trips can be changed (unticked).
        const blocked = !checked && atSelectionLimit;
        const box = (
          <Checkbox
            size="small"
            icon={<UncheckedIcon />}
            checked={checked}
            disabled={blocked}
            onChange={(e) => toggleTrip(params.row.tripID, e.target.checked)}
          />
        );
        return blocked ? (
          <Tooltip title={`A reset takes at most ${MAX_BULK_EVENT_RESET} trips`}>
            <span>{box}</span>
          </Tooltip>
        ) : box;
      },
    } as GridColDef] : []),
    {
      field: "tripID",
      headerName: "Trip #",
      width: 100,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <Link to={`/trips/${params.value}`} target="_blank" rel="noopener noreferrer" style={readableLink(boldText)}>
          {params.value}
        </Link>
      ),
    },
    {
      field: "lastEventResetAt",
      headerName: "Reset",
      width: 70,
      sortable: false,
      renderCell: (r) => <LastResetCell row={r.row} />,
    },
    { field: "clientName", headerName: "Client", width: 130 },
    { field: "hub", headerName: "Trip Location", width: 140 },
    {
      field: "controlNumber",
      headerName: "Control #",
      width: 140,
      sortable: false,
      renderCell: (r) =>
        r.row.shortKey ? (
          <Tooltip title={r.row.controlNumber}>
            <span className="table-cell-trucate">{r.row.shortKey}</span>
          </Tooltip>
        ) : (
          r.value
        ),
    },
    {
      field: "pickupDateTime",
      headerName: "Pickup Time",
      width: 160,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {params.value
            ? new Date(params.value).toLocaleString([], {
                hourCycle: "h23",
                year: "2-digit",
                month: "numeric",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : ""}
          <Typography sx={{ fontSize: 11, ml: 0.25 }}>({params.row.timeZone?.trim()})</Typography>
        </>
      ),
    },
    { field: "driverName", headerName: "Driver Name", flex: 1, minWidth: 140 },
    // driverHub, litmusStatus and closedBy are not sortable: OrderApi's DispatchQueueMapper has no
    // sort for them and returns a 500 ("DispatchQueueSort not found").
    { field: "driverHub", headerName: "Driver Location", width: 140, sortable: false },
    {
      field: "phone",
      headerName: "Phone",
      width: 130,
      sortable: false,
      renderCell: (r) => {
        const raw = (r.value as string | undefined)?.replace(/\D/g, "");
        if (!raw || raw.length !== 10) return r.value ?? "";
        return (
          <a href={`tel:+1${raw}`} style={readableLink(boldText)}>{`${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}`}</a>
        );
      },
    },
    {
      field: "driverResponse",
      headerName: "Driver Response",
      width: 180,
      sortable: false,
      renderCell: (r) => {
        const fmt = (s?: string) =>
          s ? new Date(s).toLocaleString([], { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) : null;
        const rcvd = fmt(r.row.sheetReceivedDateTime);
        const called = fmt(r.row.driverCalledDateTime);
        if (!rcvd && !called) return "";
        return (
          <Box sx={{ fontSize: 11, lineHeight: 1.2 }}>
            {rcvd && <div>Rcvd: {rcvd}</div>}
            {called && <div>Call: {called}</div>}
          </Box>
        );
      },
    },
    {
      field: "events",
      headerName: "Events",
      width: 90,
      sortable: false,
      renderCell: (r) => (
        <Link to={`/trips/${r.row.tripID}?tab=events`} target="_blank" rel="noopener noreferrer" style={readableLink(boldText)}>
          {r.row.tripID}
        </Link>
      ),
    },
    {
      field: "litmusStatus",
      headerName: "Litmus",
      width: 110,
      sortable: false,
      renderCell: (r) => {
        const v = r.value as string | undefined;
        if (!v) return "";
        const colorMap: Record<string, string> = {
          Passed: "green",
          Failed: "#d32f2f",
          Pending: readableMuted(boldText, "gray"),
          "Trip Active": readableMuted(boldText, "gray"),
        };
        return <span style={{ color: colorMap[v] ?? "inherit" }}>{v}</span>;
      },
    },
    { field: "alertText", headerName: "Alert", width: 110, sortable: false },
    { field: "closedBy", headerName: "Closed By", width: 120, sortable: false },
  ];

  // Paging and sorting keep the selection (a select-all spans every page); changing a filter
  // changes which trips match, so it clears it.
  const { pagedSearchOptions: _paging, sortAsc: _sortAsc, sortName: _sortName, ...filters } = search;
  const filterKey = JSON.stringify(filters);
  currentFilterKeyRef.current = filterKey;
  useEffect(() => {
    clearSelection();
  }, [filterKey, clearSelection]);

  useEffect(() => {
    setIsLoading(true);
    setRows([]);
    setTotal(0);
    // A newer search supersedes this one: drop its late response so it can't overwrite the grid
    // (typing a date fires a search per keystroke, and an earlier, slower one could land last).
    let superseded = false;
    ordersService
      .searchOrders(search)
      .then((results) => {
        if (superseded) return;
        setRows(results.results);
        setTotal(results.count);
      })
      .finally(() => {
        if (!superseded) setIsLoading(false);
      });
    return () => {
      superseded = true;
    };
  }, [search]);

  // Save the whole search on every change -- every filter, the dates, sort and page size --
  // so the queue comes back exactly as the closer left it.
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(search));
  }, [search]);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    setSearch({
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort === "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    });
    gridRef.current?.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize !== search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({
      ...search,
      pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page },
    });
  };

  // Re-run the search with whatever filters are currently set. Resets the
  // page to the first one so users don't end up on a phantom page after the
  // filter set changes. Reads the latest search through the updater: a background bulk reset calls
  // this when it finishes, from a poll set up with the filters as they were when the run started.
  const refreshSearch = () => {
    setSearch((current) => ({
      ...current,
      pagedSearchOptions: { ...current.pagedSearchOptions, offset: 0 },
    }));
    gridRef.current?.setPage(0);
  };

  // Some legacy Litmus reasons were written for an HTML grid and carry <font>/<br> markup.
  // Dashboard's own export stripped exactly these tags before writing each cell (the replace
  // chain in QueueForm.aspx.vb's DumpExcel), so strip them here rather than shipping raw tags
  // into a spreadsheet.
  const stripLegacyMarkup = (value?: string | null) =>
    (value ?? "")
      .replace(/<\/?font[^>]*>/gi, "")
      .replace(/<br\s*\/?>/gi, " ")
      .trim();

  // Export the current filter set to a real .xlsx. The grid is server-paginated,
  // so `rows` only holds the visible page — we re-run the same search with a
  // large page size to capture every matching trip, then flatten the same
  // columns the user sees into plain text values.
  //
  // `width` is Excel's character-width unit. The free-text error columns are
  // deliberately capped rather than sized to their content: a Litmus reason can
  // run to a paragraph, and a column wide enough for the worst one makes the
  // sheet unreadable sideways — so cap the width and let `wrap` do the work.
  //
  // `cellType` promotes a column out of text so Excel can actually work with it:
  // Trip # sorts numerically and pivots, and the two received/called timestamps
  // stop being raw ISO strings. The value functions are untouched — see toCell,
  // which coerces their output and falls back to the text if it can't.
  const exportColumns: {
    header: string;
    width: number;
    wrap?: boolean;
    cellType?: "date" | "number";
    value: (r: OrderListItem) => string;
  }[] = [
    { header: "Trip #", width: 10, cellType: "number", value: (r) => (r.tripID ?? "").toString() },
    { header: "Client", width: 20, value: (r) => r.clientName ?? "" },
    { header: "Trip Location", width: 16, value: (r) => r.hub ?? "" },
    // Mirror the grid's Control # column: show shortKey when present (the grid
    // shows controlNumber only as a tooltip fallback), else the raw controlNumber.
    { header: "Control #", width: 14, value: (r) => r.shortKey ?? r.controlNumber ?? "" },
    {
      // Deliberately NOT a date cell: this appends the trip's timezone abbreviation,
      // and a real date cell can't carry "(CST)". It stays a formatted string, which
      // is why it renders slightly differently from the two columns below.
      header: "Pickup Time",
      width: 20,
      value: (r) =>
        r.pickupDateTime
          ? `${new Date(r.pickupDateTime).toLocaleString([], {
              hourCycle: "h23",
              year: "2-digit",
              month: "numeric",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}${r.timeZone?.trim() ? ` (${r.timeZone.trim()})` : ""}`
          : "",
    },
    { header: "Driver Name", width: 22, value: (r) => r.driverName ?? "" },
    { header: "Driver Location", width: 16, value: (r) => r.driverHub ?? "" },
    { header: "Phone", width: 14, value: (r) => r.phone ?? "" },
    { header: "Sheet Received", width: 20, cellType: "date", value: (r) => r.sheetReceivedDateTime ?? "" },
    { header: "Driver Called", width: 20, cellType: "date", value: (r) => r.driverCalledDateTime ?? "" },
    { header: "Litmus", width: 12, value: (r) => r.litmusStatus ?? "" },
    // Why the Litmus status is what it is. Andrew, 2026-10-02: "Since Flow has different
    // errors, I am trying to see if there are like reasons why these are failing."
    // Dashboard's export carried this as one column literally headed `FailReason` (DumpExcel
    // dumps the bound ReviewQueue DataTable, whose FailReason column is set from the sproc's
    // AutoApproveFailReason); it had no B2B column at all - a B2B failure only showed as
    // Alert = "B2B Error" with no text. Split into two columns here because B2BErrorMessage
    // forces Failed independently and a trip can carry both: coalescing would silently drop
    // one, and it would mix two error vocabularies in a column Andrew intends to count by.
    { header: "Litmus Error", width: 50, wrap: true, value: (r) => stripLegacyMarkup(r.failReason) },
    { header: "B2B Error", width: 40, wrap: true, value: (r) => stripLegacyMarkup(r.b2BErrorMessage) },
    // Alert is a short cascade value today ("B2B Error", "Litmus Failed", ...) but it is
    // free-form enough to outgrow its column, so wrap it too rather than widening it.
    { header: "Alert", width: 22, wrap: true, value: (r) => r.alertText ?? "" },
    { header: "Closed By", width: 14, value: (r) => r.closedBy ?? "" },
  ];

  // Excel only auto-fits a row when the row carries no stored height, and that
  // auto-fit is unreliable for wrapped cells across Excel versions — so set the
  // height ourselves on the rows that actually need more than one line. Capped at
  // MAX_WRAPPED_LINES so one pathological Litmus reason can't produce a
  // half-screen row; the full text is still in the cell either way.
  const POINTS_PER_LINE = 15; // Excel's default row height at Calibri 11
  const MAX_WRAPPED_LINES = 8;

  const wrappedLineCount = (text: string, width: number) =>
    text.split("\n").reduce(
      // Divide by width - 1 rather than width: that leaves a character of slack for
      // cell padding, so we round a borderline line up instead of clipping it.
      (lines, line) => lines + Math.max(1, Math.ceil(line.length / (width - 1))),
      0
    );

  // 24-hour, to match how the Pickup Time column renders (hourCycle h23).
  const DATE_FORMAT = "m/d/yy hh:mm";

  // The timestamps arrive as offset-free wall clock ("2026-09-30T18:22:11") because
  // the API serializes a DateTime of Unspecified kind. exceljs converts a JS Date to
  // an Excel serial off getTime(), i.e. off the UTC instant — so `new Date(text)`,
  // which parses an offset-free string as *local*, would land in the sheet shifted by
  // the viewer's UTC offset. Reading the parts and rebuilding them via Date.UTC keeps
  // the wall clock we were given and makes the output independent of the browser's
  // timezone, which is what you want in a report two people compare side by side.
  const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

  const toCell = (text: string, cellType?: "date" | "number"): string | number | Date | null => {
    if (!cellType) return text;
    // A missing value must be a genuinely empty cell — not "", and not the 1900 epoch.
    if (text === "") return null;
    if (cellType === "number") {
      const parsed = Number(text);
      return Number.isFinite(parsed) ? parsed : text;
    }
    const parts = WALL_CLOCK.exec(text);
    // Fall back to the raw string rather than dropping a value we can't parse: an ugly
    // cell beats a missing one when someone is auditing why a trip failed.
    if (!parts) return text;
    const [, y, mo, d, h, mi, s] = parts;
    return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s ?? 0)));
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportSearch: OrderSearch = {
        ...search,
        // Pull every matching trip in one shot rather than the visible page.
        pagedSearchOptions: { offset: 0, pageSize: Math.max(total, 1) },
      };
      const results = await ordersService.searchOrders(exportSearch);
      const data = results.results ?? [];

      // exceljs is ~1MB minified, so import it on demand: webpack gives it its own
      // lazy chunk and the main bundle is unchanged for everyone who never exports.
      const ExcelJS = await import("exceljs");
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Review Queue");

      // Defining columns with a header also writes row 1, so data rows start at 2.
      sheet.columns = exportColumns.map((c) => ({ header: c.header, width: c.width }));
      sheet.getRow(1).font = { bold: true };
      // Freeze the header so it stays put while scrolling, and put filter dropdowns
      // on it — Excel extends the filter over the contiguous rows beneath on its own.
      sheet.views = [{ state: "frozen", ySplit: 1 }];
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: exportColumns.length },
      };

      data.forEach((item) => {
        // `values` is the text the column has always produced — still the basis for the
        // wrap maths. `cells` is that same text coerced to the type Excel should store.
        const values = exportColumns.map((c) => c.value(item));
        const cells = exportColumns.map((c, i) => toCell(values[i], c.cellType));
        const row = sheet.addRow(cells);
        let lines = 1;
        exportColumns.forEach((c, i) => {
          const cell = row.getCell(i + 1);
          // Format only the cells that really became dates: a value that failed to parse
          // fell back to its raw string and must not be given a date format.
          if (cells[i] instanceof Date) cell.numFmt = DATE_FORMAT;
          if (!c.wrap) return;
          cell.alignment = { wrapText: true, vertical: "top" };
          lines = Math.max(lines, wrappedLineCount(values[i], c.width));
        });
        if (lines > 1) {
          row.height = Math.min(lines, MAX_WRAPPED_LINES) * POINTS_PER_LINE;
        }
      });

      const blob = new Blob([await workbook.xlsx.writeBuffer()], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `review-queue-${moment().format("YYYYMMDD-HHmmss")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  // A queued run's progress, read back from its batch until the server finishes it.
  useEffect(() => {
    if (!queuedBatchId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const detail = await EventResetsService.getBatch(queuedBatchId);
        if (stopped) return;
        const b = detail.batch;
        const running = detail.trips.filter((t) => t.status === "Running").length;
        if (isBatchFinished(detail)) {
          setQueuedBatchId(null);
          setResetRun(null);
          setResetProgress(null);
          setOverlayInBackground(false);
          setResetSummary(summaryFromBatch(detail));
          refreshSearch();
          return;
        }
        setResetRun((prev) => ({
          done: Math.max(0, b.tripCount - b.pendingCount),
          through: 0,
          total: b.tripCount,
          queued: { ...(prev?.queued ?? { batchId: queuedBatchId }), batchId: queuedBatchId, running, cancelled: b.status === "Cancelled" || prev?.queued?.cancelled },
        }));
        setResetProgress(`Resetting ${Math.max(0, b.tripCount - b.pendingCount)} of ${b.tripCount}…`);
      } catch {
        // A missed poll is retried on the next tick; the run itself is server-side.
      }
    };
    poll();
    const timer = setInterval(poll, QUEUED_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
    // Keyed on the batch alone. The refreshSearch this closes over is from when the run started, which
    // is safe because it reads the current search through setSearch's updater; it leaves the filters,
    // and so any selection the admin has made since, alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedBatchId]);

  const cancelQueuedRun = async () => {
    if (!queuedBatchId) return;
    setResetRun((prev) => (prev?.queued ? { ...prev, queued: { ...prev.queued, cancelling: true } } : prev));
    try {
      const result = await EventResetsService.cancelBatch(queuedBatchId);
      toast.info(`Bulk reset cancelled. ${result?.message ?? ""}`.trim());
      setResetRun((prev) => (prev?.queued ? { ...prev, queued: { ...prev.queued, cancelling: false, cancelled: true } } : prev));
    } catch (e: any) {
      toast.error(responseErrorText(e) || "Could not cancel the bulk reset.");
      setResetRun((prev) => (prev?.queued ? { ...prev, queued: { ...prev.queued, cancelling: false } } : prev));
    }
  };

  const handleBulkEventReset = async () => {
    // The button is disabled outside 1..MAX, but re-check: this is the last stop before a
    // destructive call.
    if (selectionError) {
      toast.error(selectionError);
      return;
    }
    const tripIds = [...selectedTripIds];

    // Queued path: the whole selection is one submission; the server resets the trips and this
    // page follows the batch. Falls back to the chunked path where OrderApi has no queue.
    setResetProgress("Submitting…");
    try {
      const submitted = await EventResetsService.submitQueuedBulkReset(tripIds);
      const note = queuedSubmitMessage(submitted);
      toast.success(note);
      clearSelection();
      setResetRun({
        done: submitted.skipped.length,
        through: 0,
        total: submitted.queued + submitted.skipped.length,
        queued: { batchId: submitted.batchId, running: 0, note },
      });
      setOverlayInBackground(false);
      setQueuedBatchId(submitted.batchId);
      return;
    } catch (e: any) {
      if (!isQueueUnavailable(e)) {
        setResetProgress(null);
        toast.error(responseErrorText(e) || bulkResetErrorMessage(e));
        return;
      }
    }

    setResetRun({ done: 0, through: Math.min(tripIds.length, BULK_EVENT_RESET_CHUNK_SIZE), total: tripIds.length });
    try {
      const summary = await runBulkEventReset(tripIds, (chunk, batchId, isFinal) => EventsService.bulkResetTripEvents(chunk, batchId, isFinal), (through, count) => {
        setResetProgress(`Resetting ${through} of ${count}…`);
        // Chunks start on multiples of the chunk size, so everything before this chunk is done.
        const done = Math.floor((through - 1) / BULK_EVENT_RESET_CHUNK_SIZE) * BULK_EVENT_RESET_CHUNK_SIZE;
        setResetRun({ done, through, total: count });
      });
      setResetSummary(summary);
    } finally {
      setResetRun(null);
      setResetProgress(null);
      clearSelection();
      // Refresh whatever happened: even a run that stopped early may have reset some trips.
      refreshSearch();
    }
  };

  const LoadingSkeleton = () => (
    <Box sx={{ height: "fit-content", padding: 0, margin: 0, overflow: "hidden" }}>
      {[...Array(search.pagedSearchOptions.pageSize)].map((_, i) => (
        <Skeleton key={i} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  // Multi-select that shows a placeholder when nothing is picked, and a comma
  // list (or "N selected") otherwise. Values are objects with .id/.name so they
  // round-trip through ordersService which calls .map(o => o.id).
  const renderMultiSelect = (
    items: any[],
    selected: any[] | null | undefined,
    placeholder: string,
    onChange: (v: any[] | null) => void,
  ) => {
    // Drive MUI's selection by id, not by object reference. MUI uses strict
    // equality to decide whether a clicked MenuItem is already in the value
    // array — and the parent-supplied object references can change between
    // renders, which made the second click on a selected item re-add it
    // instead of removing it. Tracking ids round-trips cleanly and we map
    // back to objects on the way out so callers don't have to change.
    const selectedIds = ((selected ?? []) as any[]).map((s) => (typeof s === "object" ? s.id : s));
    return (
      <FormControl size="small" sx={inputSx}>
        <Select
          multiple
          displayEmpty
          value={selectedIds}
          input={<OutlinedInput />}
          onChange={(e) => {
            const nextIds = e.target.value as any[];
            // "Select all" entry: checks every item in this dropdown in one click.
            if (nextIds.includes(SELECT_ALL_VALUE)) {
              onChange(items.length === 0 ? null : [...items]);
              return;
            }
            // "Clear all" entry: unchecks everything in this dropdown in one click.
            if (nextIds.includes(CLEAR_ALL_VALUE)) {
              onChange(null);
              return;
            }
            const nextObjects = nextIds
              .map((id) => items.find((it) => it.id === id))
              .filter(Boolean);
            onChange(nextObjects.length === 0 ? null : nextObjects);
          }}
          renderValue={(picked) => {
            const ids = picked as any[];
            if (!ids || ids.length === 0)
              return <em style={{ color: "#666", fontStyle: "normal" }}>{placeholder}</em>;
            const objs = ids.map((id) => items.find((it) => it.id === id)).filter(Boolean);
            if (objs.length === 1) return objs[0].name;
            if (objs.length <= 3) return objs.map((o) => o.name).join(", ");
            return `${ids.length} selected`;
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
        >
          <MenuItem
            value={SELECT_ALL_VALUE}
            disabled={items.length === 0 || items.every((it) => selectedIds.includes(it.id))}
            sx={{ fontStyle: "italic" }}
          >
            <ListItemText disableTypography primary="Select all" />
          </MenuItem>
          <MenuItem
            value={CLEAR_ALL_VALUE}
            disabled={selectedIds.length === 0}
            sx={{ borderBottom: "1px solid", borderColor: "divider", fontStyle: "italic" }}
          >
            <ListItemText disableTypography primary="Clear all" />
          </MenuItem>
          {items.map((it) => (
            <MenuItem key={it.id} value={it.id}>
              <Checkbox size="small" checked={selectedIds.includes(it.id)} />
              <ListItemText disableTypography primary={it.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 0, borderRadius: "4px 4px 0 0" }}>
        {/* Layout mirrors legacy Dashboard QueueForm.aspx: 2-column grid of label+control
            rows, then a date-range row, then a refresh button + record count. */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ color: "#8B4513", backgroundColor: "#fff5cc", px: 2, py: 0.5, width: "100%", border: "1px solid #d0c060" }}>
            Trip Review Queue
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "grid",
            // Three label+control pairs per row (was two) so the filters take less height on the small
            // monitors the closers use and more of the grid shows without scrolling.
            gridTemplateColumns: `max-content ${INPUT_WIDTH}px max-content ${INPUT_WIDTH}px max-content ${INPUT_WIDTH}px`,
            columnGap: 1.5,
            rowGap: 0.75,
            alignItems: "center",
            // Center the whole filter grid horizontally inside the Paper, the
            // way Dashboard QueueForm renders inside its centered <table>.
            width: "fit-content",
            mx: "auto",
            border: "1px solid #d0d0d0",
            borderRadius: 1,
            p: 2,
          }}
        >
          {/* Barge. The legacy queue carried these as "*Barge Clients" / "*Non-Barge Clients"
              entries inside its Client dropdown; a filter of its own is clearer and leaves the
              Client multi-select to do one job. Non-Barge means Client.IsBargeClient = 0, which
              is what the legacy proc compares - short-line clients are included in it. */}
          <Typography variant="body2" align="right">Client Type:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              value={triValue(search.isBargeClient)}
              onChange={(e) => setSearch({ ...search, isBargeClient: triParse(e.target.value as string), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v === "true" ? "Barge" : "Non-Barge"))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Barge</MenuItem>
              <MenuItem value="false">Non-Barge</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" align="right">Trip Location:</Typography>
          {renderMultiSelect(hubs, search.hubIds, "All Locations", (v) =>
            setSearch({ ...search, hubIds: v, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })
          )}
          <Typography variant="body2" align="right">Client:</Typography>
          {renderMultiSelect(visibleClients, search.clientIds, "All", (v) =>
            setSearch({ ...search, clientIds: v, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })
          )}

          <Typography variant="body2" align="right">Trips:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              value={triValue(search.isMpv)}
              onChange={(e) => setSearch({ ...search, isMpv: triParse(e.target.value as string), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v === "true" ? "MPV Only" : "Non-MPV Only"))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">MPV Only</MenuItem>
              <MenuItem value="false">Non-MPV Only</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" align="right">Litmus:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              value={search.litmusFilter ?? ""}
              onChange={(e) => setSearch({ ...search, litmusFilter: e.target.value === "" ? null : (e.target.value as string), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v as string))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="Passed">Passed</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Trip Active">Trip Active</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" align="right">Driver Response:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              value={search.driverResponse ?? ""}
              onChange={(e) => setSearch({ ...search, driverResponse: e.target.value === "" ? null : (e.target.value as string), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v as string))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Received">Received</MenuItem>
              <MenuItem value="Not Received">Not Received</MenuItem>
              <MenuItem value="Called">Called</MenuItem>
              <MenuItem value="Not Called">Not Called</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" align="right">Alert:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              // "Near OTP" isn't a server alert string — it's the isNearOtp boolean
              // param (shared with Order Management). Surface it in this dropdown but
              // route it to isNearOtp, keeping `alert` and isNearOtp mutually exclusive.
              value={search.isNearOtp ? "Near OTP" : (search.alert ?? "")}
              onChange={(e) => {
                const v = e.target.value as string;
                setSearch({
                  ...search,
                  isNearOtp: v === "Near OTP" ? true : null,
                  alert: v === "" || v === "Near OTP" ? null : v,
                  pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
                });
              }}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v as string))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="15+ hours">15+ hours</MenuItem>
              <MenuItem value="Audit">Audit</MenuItem>
              <MenuItem value="B2B Error">B2B Error</MenuItem>
              <MenuItem value="BNSF Late">BNSF Late</MenuItem>
              <MenuItem value="Canceled">Canceled</MenuItem>
              <MenuItem value="Forced">Forced</MenuItem>
              <MenuItem value="Mapped">Mapped</MenuItem>
              <MenuItem value="Near OTP">Near OTP</MenuItem>
              <MenuItem value="No Events">No Events</MenuItem>
              <MenuItem value="No Shift">No Shift</MenuItem>
              <MenuItem value="Protection">Protection</MenuItem>
              <MenuItem value="UP Late">UP Late</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" align="right">Driver Location:</Typography>
          {renderMultiSelect(hubs, search.driverHubIds, "All Locations", (v) =>
            setSearch({ ...search, driverHubIds: v, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })
          )}
          <Typography variant="body2" align="right">Closed By:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              value={search.closedByName ?? ""}
              onChange={(e) => setSearch({ ...search, closedByName: e.target.value === "" ? null : (e.target.value as string), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v as string))}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All</MenuItem>
              {closedByNames.map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" align="right">Sort by:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              value={search.sortName ?? "pickupDateTime"}
              onChange={(e) => setSearch({ ...search, sortName: e.target.value as string, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
            >
              <MenuItem value="hub">Trip Location</MenuItem>
              <MenuItem value="tripID">Trip Number</MenuItem>
              <MenuItem value="controlNumber">Control Number</MenuItem>
              <MenuItem value="pickupDateTime">Pickup Time</MenuItem>
              <MenuItem value="driverName">Driver Name</MenuItem>            </Select>
          </FormControl>
          <Typography variant="body2" align="right">Accuracy Verified:</Typography>
          <FormControl size="small" sx={inputSx}>
            <Select
              displayEmpty
              value={triValue(search.isVerified)}
              onChange={(e) => setSearch({ ...search, isVerified: triParse(e.target.value as string), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
              renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : (v === "true" ? "Yes" : "No"))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          {canResetEvents && (
            <>
              <Typography variant="body2" align="right">Event Reset:</Typography>
              <FormControl size="small" sx={inputSx}>
                <Select
                  displayEmpty
                  value={(search.eventResetFilter ?? "") as string}
                  onChange={(e) => setSearch({ ...search, eventResetFilter: e.target.value === "" ? null : (e.target.value as EventResetFilter), pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
                  renderValue={(v) => (v === "" ? <em style={{ color: "#666", fontStyle: "normal" }}>All</em> : EVENT_RESET_FILTERS.find((f) => f.value === v)?.label)}
                >
                  <MenuItem value="">All</MenuItem>
                  {EVENT_RESET_FILTERS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" align="right">Resettable:</Typography>
              {/* Checked: only trips a bulk Event Reset would take. Unchecked: everything. */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={search.isResettable === true}
                  onChange={(e) => setSearch({ ...search, isResettable: e.target.checked ? true : null, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
                />
              </Box>
            </>
          )}

          {/* nowrap + shrinkable pickers: two fixed 220px fields plus "to" are wider than the
              columns this row spans, so the second picker dropped onto its own line. The fields
              now shrink to fit and only reach INPUT_WIDTH when there is room, keeping them lined
              up with the inputs above without ever breaking the row. */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ gridColumn: "1 / -1", flexWrap: "nowrap", minWidth: 0, justifyContent: "center" }}>
            <Typography variant="body2" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>Date Range:</Typography>
            <DatePicker
              slotProps={{ textField: { size: "small", sx: { flexShrink: 1, minWidth: 0, width: INPUT_WIDTH } } }}
              value={search.startDate}
              onChange={(v) => (!v || isCompleteDate(v)) && setSearch({ ...search, startDate: v ? moment(v).startOf("day") : null, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
            />
            <Typography variant="body2" sx={{ flexShrink: 0 }}>to</Typography>
            <DatePicker
              slotProps={{ textField: { size: "small", sx: { flexShrink: 1, minWidth: 0, width: INPUT_WIDTH } } }}
              value={search.endDate}
              onChange={(v) => (!v || isCompleteDate(v)) && setSearch({ ...search, endDate: v ? moment(v).startOf("day") : null, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } })}
            />
            <Button size="small" variant="contained" onClick={refreshSearch}>
              Refresh
            </Button>
          </Stack>
        </Box>
      </Paper>
      <Box sx={{ overflow: "hidden" }}>
        <Paper sx={{ width: "100%", height: isLoading ? "100vh" : "auto", overflow: "hidden", borderRadius: "0 0 4px 4px" }}>
          {/* Table header: what the results are and what you can do with them belong to the table,
              not to the filter block that produced them. */}
          {queuedBatchId && overlayInBackground && resetRun && (
            <Alert
              severity="info"
              sx={{ borderRadius: 0 }}
              data-testid="bulk-reset-background-banner"
              action={
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={() => setOverlayInBackground(false)}>
                    Show progress
                  </Button>
                  <Button color="inherit" size="small" onClick={() => navigate(eventResetsLink(queuedBatchId))}>
                    Event Resets
                  </Button>
                </Stack>
              }
            >
              Bulk reset running in the background: {resetRun.done} of {resetRun.total} trips done.
            </Alert>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}
          >
            {/* Selection actions act on the checked trips only, so they live with the selection
                and appear only while something is checked. Export acts on the whole filtered
                list and keeps its own button on the right. */}
            {canResetEvents ? (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {isSelectingAll ? (
                    <Stack component="span" direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={14} />
                      <span>Finding trips that can be reset…</span>
                    </Stack>
                  ) : (
                    `${selectedTripIds.length} selected${atSelectionLimit ? ` (max ${MAX_BULK_EVENT_RESET})` : ""}`
                  )}
                </Typography>
                <MDButton
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<RestartAltIcon />}
                  onClick={() => setResetConfirmOpen(true)}
                  disabled={isSelectingAll || resetProgress !== null || selectionError !== null}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {resetProgress ?? "Reset events"}
                </MDButton>
                {resetProgress === null && selectedTripIds.length > 0 && (
                  <MDButton variant="text" color="secondary" size="small" onClick={clearSelection} sx={{ whiteSpace: "nowrap" }}>
                    Clear selection
                  </MDButton>
                )}
                {selectedTripIds.length > 0 && selectionError && (
                  <Typography variant="body2" color="error" noWrap>
                    {selectionError}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={<GridOnIcon />}
              onClick={exportToExcel}
              disabled={isExporting || total === 0}
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                fontWeight: 600,
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#1b5e20" },
                "&.Mui-disabled": { backgroundColor: "#c8e6c9", color: "#33691e" },
              }}
            >
              {isExporting ? "Exporting…" : "Export all"}
            </Button>
            <Typography variant="body2" sx={{ color: "#8B4513", fontWeight: 500, whiteSpace: "nowrap" }}>
              {total.toLocaleString()} record{total === 1 ? "" : "s"}
            </Typography>
          </Stack>
          <DataGrid
            autoHeight={!isLoading}
            getRowId={(row) => row.tripID}
            sx={{
              overflow: "hidden",
              fontSize: "1em",
              // 500 weight in black when the user turned on "Bold, darker text".
              ...readableText(boldText),
            }}
            rows={rows}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: { paginationModel: search.pagedSearchOptions },
              sorting: {
                sortModel: [{ field: search.sortName, sort: search.sortAsc ? "asc" : "desc" }],
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  No trips in the review queue
                </Stack>
              ),
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
          />
        </Paper>
      </Box>
      {canResetEvents && (
        <>
          <ConfirmDialog
            open={resetConfirmOpen}
            setOpen={setResetConfirmOpen}
            title="Reset events"
            subTitle={bulkResetConfirmText(selectedTripIds.length)}
            onAccept={handleBulkEventReset}
          />
          <BulkEventResetResultsDialog summary={resetSummary} onClose={() => setResetSummary(null)} />
          <BulkEventResetOverlay
            run={overlayInBackground ? null : resetRun}
            onBackground={queuedBatchId ? () => setOverlayInBackground(true) : undefined}
            onCancel={queuedBatchId ? cancelQueuedRun : undefined}
          />
        </>
      )}
    </>
  );
};

export default ReviewQueue;
